import type { TrendWeek } from '@testflow/shared';
import { useTranslation } from 'react-i18next';

// #97: Gestapelte Wochen-Säulen (Blocker unten, Hoch darüber) aus dem Trend-Endpoint
// (/reports/trend). "Muster SeverityBars invertiert" (Task-9-Brief): SeverityBars stapelt
// horizontale Balken (Breite proportional), hier ist es dieselbe Idee gedreht — vertikale Säulen,
// Höhe proportional zum WOCHEN-Maximum (Summe Blocker+Hoch über alle Wochen), damit die Säulen
// untereinander vergleichbar bleiben (nicht je Säule auf 100% normiert). KEINE Chart-Bibliothek —
// zwei ineinandergestapelte Divs mit Prozent-Höhe, wie SeverityBars zwei Divs mit Prozent-Breite.
function weekNumber(week: string): string {
  const idx = week.indexOf('-W');
  return idx === -1 ? week : week.slice(idx + 2);
}

export function TrendBars({ weeks }: { weeks: TrendWeek[] }) {
  const { t } = useTranslation();

  // Unter zwei Wochen gibt es keinen Trend zu zeigen (eine einzelne Säule wäre kein Verlauf) —
  // Hinweiszeile statt eines irreführenden Achsenkreuzes mit nur einem Punkt.
  if (weeks.length < 2) {
    return <p className="text-sm text-text-muted">{t('reporting.trend.empty')}</p>;
  }

  const max = Math.max(1, ...weeks.map((w) => w.openBlocker + w.openHigh));

  return (
    <div className="flex items-end gap-4 overflow-x-auto">
      {weeks.map((w) => {
        const total = w.openBlocker + w.openHigh;
        const blockerPct = (w.openBlocker / max) * 100;
        const highPct = (w.openHigh / max) * 100;
        return (
          <div
            key={w.week}
            data-testid={`trend-col-${w.week}`}
            className="flex flex-col items-center gap-1 shrink-0"
          >
            <span className="text-xs font-bold tabular-nums text-text-primary">{total}</span>
            <div
              className="flex h-24 w-8 flex-col-reverse overflow-hidden rounded-chip bg-bg-subtle"
              role="img"
              aria-label={t('reporting.trend.barLabel', {
                blocker: w.openBlocker,
                high: w.openHigh,
              })}
            >
              <div className="bg-[var(--sev-blocker-solid)]" style={{ height: `${blockerPct}%` }} />
              <div className="bg-[var(--sev-high-solid)]" style={{ height: `${highPct}%` }} />
            </div>
            <span className="text-[10.5px] text-text-muted">
              {t('reporting.chart.weekTick', { week: weekNumber(w.week) })}
            </span>
          </div>
        );
      })}
    </div>
  );
}
